import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Box, Grid, Typography } from "@mui/material";

// Define constants directly in the component to avoid import issues
const RACE_LABELS = {
  0: 'All Races',
  1: 'White',
  2: 'Black',
  3: 'Native American',
  4: 'Asian',
  5: 'Pacific Islander',
  6: 'Two or More',
  7: 'White (Combination)',
  8: 'Black (Combination)',
  9: 'Native American (Combination)',
  10: 'Asian (Combination)',
  11: 'Pacific Islander (Combination)'
};

const AGE_GROUP_LABELS = {
  1: '(0-4ys)',
  2: '(5-9ys)',
  3: '(10-14ys)',
  4: '(15-19ys)', 
  5: '(20-24ys)',
  6: '(25-29ys)',
  7: '(30-34ys)',
  8: '(35-39ys)',
  9: '(40-44ys)',
  10: '(45-49ys)',
  11: '(50-54ys)',
  12: '(55-59ys)',
  13: '(60-64ys)',
  14: '(65-69ys)',
  15: '(70-74ys)',
  16: '(75-79ys)',
  17: '(80-84ys)',
  18: '(85+ys)'
};

const CensusBarChart = ({ data, breakdowns, selectedStateName }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null; 
  }

  console.log("Data CensusBarChart:", data);

  // Filter data to only include valid age groups (0-17)
  const validData = data.filter(item => {
    const ageGroupNum = parseInt(item.ageGroup);
    return !isNaN(ageGroupNum) && ageGroupNum >= 1 && ageGroupNum <= 17;
  });

  // Group data by age group and aggregate values
  const groupedData = [];
  const ageGroupMap = {};

  validData.forEach((item, index) => {
    const ageGroup = item.ageGroup;
    if (!ageGroupMap[ageGroup]) {
      //console.log(`Creating new entry for age group ${ageGroup}`);
      ageGroupMap[ageGroup] = {
        ageGroup,
        population: 0,
        race: {},
        sex: {}
      };
      groupedData.push(ageGroupMap[ageGroup]);
    }

    const currentGroup = ageGroupMap[ageGroup];
    const hasBreakdowns = (item.race && Object.keys(item.race).length > 0) ||
                         (item.sex && Object.keys(item.sex).length > 0);
    
    if (!hasBreakdowns) {
      //console.log(`Adding base population: ${item.population || 0}`);
      currentGroup.population += item.population || 0;
    }
    
    // Process race data
    if (item.race) {
      //console.log("Processing race breakdowns:");
      Object.entries(item.race).forEach(([raceCode, value]) => {
        if (raceCode !== '0') {
          //console.log(`- Race ${raceCode}: ${value}`);
          if (!currentGroup.race[raceCode]) {
            currentGroup.race[raceCode] = 0;
          }
          currentGroup.race[raceCode] += value;
          currentGroup.population += value;
          //console.log(`  Added to race. New total: ${currentGroup.race[raceCode]}`);
        } else {
          //console.log(`- Skipping race code 0 (All Races)`);
        }
      });
    }

    // Process sex data
    if (item.sex) {
      //console.log("Processing sex breakdowns:");
      Object.entries(item.sex).forEach(([sexCode, value]) => {
        if (sexCode !== '0') {
          //console.log(`- Sex ${sexCode}: ${value}`);
          if (!currentGroup.sex[sexCode]) {
            currentGroup.sex[sexCode] = 0;
          }
          currentGroup.sex[sexCode] += value;
          currentGroup.population += value;
          //console.log(`  Added to sex. New total: ${currentGroup.sex[sexCode]}`);
        } else {
          //console.log(`- Skipping sex code 0 (All Sexes)`);
        }
      });
    }
  });

  // Sort data by age group
  const sortedData = groupedData.sort((a, b) => parseInt(a.ageGroup) - parseInt(b.ageGroup));
  
  console.log("Processed data:", sortedData);

  // Define color arrays outside JSX for clarity
  //const raceColors = ['#4dc9f0', '#f67019', '#537bc4', '#acc236', '#166a8f', '#f53794', '#4A3596', '#F2736A', '#8549ba'];
  //const raceColors = ['#8A2BE2', '#00FF7F', '#FF4500', '#FF1493', '#FFD700', '#00BFFF', '#FF69B4', '#32CD32', '#FF6347'];
  //const raceColors = ['#4dc9f0', '#f67019', '#f53794', '#537bc4', '#acc236', '#166a8f', '#00a950', '#58595b', '#8549ba'];
  //const raceColors = ['#E38ED2', '#C75696', '#9D3978', '#512E67', '#211C84', '#4D55CC', '#7A73D1', '#B5A8D5', '#AA60C8'];
  
  //const raceColors = ['#FF5733', '#FFC300', '#36A2EB', '#4CAF50', '#9C27B0', '#FF9800', '#E91E63', '#795548', '#607D8B'];
  //const raceColors = ['#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF', '#DAB6FC', '#FFC6FF', '#C6F7FF', '#FFD3B6'];
  //const raceColors = ['#3E2723', '#5D4037', '#455A64', '#1B5E20', '#4A148C', '#B71C1C', '#F57F17', '#0D47A1', '#006064'];
  const raceColors = ["#7cd7ec", "#1986a2", "#cdc2a2", "#f7f7b3", "#f2ab4b", "#da3a27", "#bb312d", "#7cd7ec", "#1986a2"];
  
  const sexColors = { '1': '#4dc9f0', '2': '#f53794' };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%", height: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: { xs: "start", sm: "start", md: "center" }, width: "95%", height: "90%", textAlign: "center", marginTop: { xs: "3.5rem", sm: "1rem", md: "0" }, ml: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ textAlign: "center" }}>
              Population Demographics for {selectedStateName}
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={sortedData} margin={{ top: 20, right: 30, left: 60, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="ageGroup" 
                  angle={-45} 
                  textAnchor="end"
                  tickFormatter={(value) => AGE_GROUP_LABELS[parseInt(value)] || value}
                  height={80}
                  tick={{ fontSize: 12 }}
                  interval={0}
                  padding={{ left: 10, right: 10 }}
                />
                <YAxis 
                  tickFormatter={(t) => t.toLocaleString()}
                  width={80}
                  tick={{ fontSize: 10 }}
                />
                <Tooltip 
                  formatter={(value) => value.toLocaleString()}
                  labelFormatter={(label) => `Age Group: ${AGE_GROUP_LABELS[parseInt(label)] || label}`}
                />
                <Legend />
                {breakdowns.race && sortedData[0] && Object.keys(sortedData[0]?.race || {}).map((raceCode, idx) => (
                  <Bar 
                    key={raceCode}
                    dataKey={`race.${raceCode}`}
                    name={RACE_LABELS[raceCode] || `Race ${raceCode}`}
                    fill={raceColors[idx % raceColors.length] || '#cccccc'}
                    stackId="race"
                  />
                ))}
                {breakdowns.sex && sortedData[0] && Object.keys(sortedData[0]?.sex || {}).map((sexCode) => (
                  (sexCode === '1' || sexCode === '2') && (
                    <Bar
                      key={sexCode}
                      dataKey={`sex.${sexCode}`}
                      name={sexCode === '1' ? 'Male' : 'Female'}
                      fill={sexColors[sexCode] || '#cccccc'}
                      stackId="sex"
                    />
                  )
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default CensusBarChart; 